import React, { createContext, useContext, useState, useEffect } from 'react';
import { useImageUpload, EventImage } from '@/hooks/useImageUpload';
import { EventType } from '@/utils/eventUtils';

interface EventImagesContextType {
  images: EventImage[];
  loading: boolean;
  error: string | null;
  getImagesByCategory: (category: string) => EventImage[];
  refetch: () => void;
}

const EventImagesContext = createContext<EventImagesContextType | undefined>(undefined);

interface EventImagesProviderProps {
  children: React.ReactNode;
  eventId: string;
  eventType: EventType;
}

export function EventImagesProvider({ children, eventId, eventType }: EventImagesProviderProps) {
  const [images, setImages] = useState<EventImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { fetchEventImages } = useImageUpload({ 
    eventId, 
    eventType 
  });

  const loadImages = async () => {
    if (!eventId) return;
    
    try {
      setLoading(true);
      setError(null);
      const fetchedImages = await fetchEventImages(true); // Include inactive images
      setImages(fetchedImages);
    } catch (err) {
      console.error('Error loading event images:', err);
      setError('Failed to load images');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadImages();
  }, [eventId, eventType]);

  const getImagesByCategory = (category: string) => {
    const filtered = images.filter(img => {
      // Filter by category and active status
      if (img.category !== category || !img.is_active) return false;
      
      // Validate URL
      const url = img.publicUrl || '';
      const isValid = url && (
        url.startsWith('https://') || 
        url.startsWith('http://') || 
        url.startsWith('data:') ||
        url.startsWith('blob:')
      );
      
      if (!isValid) {
        console.warn('[EventImagesContext] Filtered invalid URL:', url, 'for image:', img.id);
      }
      
      return isValid;
    }).sort((a, b) => a.display_order - b.display_order);
    
    // Deduplicate by publicUrl
    const seen = new Set<string>();
    return filtered.filter(img => {
      const url = img.publicUrl || '';
      if (seen.has(url)) return false;
      seen.add(url);
      return true;
    });
  };

  const contextValue: EventImagesContextType = {
    images,
    loading,
    error,
    getImagesByCategory,
    refetch: loadImages
  };

  return (
    <EventImagesContext.Provider value={contextValue}>
      {children}
    </EventImagesContext.Provider>
  );
}

export function useEventImages() {
  const context = useContext(EventImagesContext);
  if (context === undefined) {
    throw new Error('useEventImages must be used within an EventImagesProvider');
  }
  return context;
}